// NavigationBar.tsx
import * as React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  Typography,
  Tooltip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { fetchEmployeeRoleByAuthUserId, getCurrentSession, onAuthStateChange, signOut } from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import { loadCachedRole, saveCachedRole } from "../pages/utils/Utils";
import type { CompanyRole } from "../types/navigation";
import type { NavItem, NavProps } from "../types/navigation";


export default function NavigationBar({
  currentPage,
  handlePageChange,
  onHelpClick,
}: NavProps) {
  const { lang, setLang, t } = useI18n();
  const theme = useTheme();
  // Below "lg" rather than "md": the desktop cluster (nav items + switch
  // account + language toggle + emergency + profile) doesn't fit down to
  // 900px -- it visibly overlaps on tablets like a 1024px iPad Pro. Tablets
  // get the hamburger menu instead, same as phones.
  const isTabletDown = useMediaQuery(theme.breakpoints.down("lg"));

  // MAIN (center group)
  const employeeItems: NavItem[] = [
    { label: t("nav.kontrolScore"), page: "KontrolScore", icon: <DashboardOutlinedIcon /> },
    { label: t("nav.actionPlan"), page: "ActionPlan", icon: <AssignmentOutlinedIcon /> },
  ];

  // RIGHT actions
  const itemProfile: NavItem = { label: t("nav.profile"), page: "Profile", icon: <PersonOutlineIcon /> };
  const itemEmergency: NavItem = { label: t("nav.emergency"), page: "Emergency", icon: <WarningAmberOutlinedIcon /> };

  const isSelected = (page: string) => currentPage === page;

  const go = (page: string) => {
    handlePageChange(page);
    setAnchorEl(null); // closes mobile menu if open
  };

  const handleLogout = async () => {
    // Signs out whatever local session is active -- also the way out of a
    // stuck session where the account was deleted after this browser last
    // logged in, since every authenticated fetch would otherwise fail with
    // no way back to a working login screen.
    await signOut();
    localStorage.removeItem("tk.currentPage");
    localStorage.removeItem("tk.nav.role");
    setAnchorEl(null);
    handlePageChange("Entry");
  };

  // mobile menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const bg = "linear-gradient(180deg, #E5E7EB 0%, #DDE1E7 100%)"; // grey, no blue

  const navBtnSx = (selected: boolean) => ({
    textTransform: "none",
    fontWeight: selected ? 900 : 800,
    borderRadius: 999,
    px: 1.5,
    py: 0.9,
    color: "#111827",
    minHeight: 42,
    bgcolor: selected ? "#11182712" : "transparent",
    "&:hover": { bgcolor: "#11182714" },
    "& .MuiButton-startIcon": { color: selected ? "#111827" : "#111827B3" },
  });

  /* ---------------- AUTHZ: load role ---------------- */
  const cachedRole = React.useMemo(() => loadCachedRole(), []);
  const [role, setRole] = React.useState<CompanyRole>(cachedRole ?? "employee");
  const [roleLoading, setRoleLoading] = React.useState(!cachedRole);

  React.useEffect(() => {
    async function loadRole() {
      setRoleLoading(true);
      try {
        const { data: sess } = await getCurrentSession();
        const session = sess.session;

        // No session -> public state, don't query DB
        if (!session?.user?.id) {
          setRole("employee");
          return;
        }

        const userId = session.user.id;

        const { data: emp, error } = await fetchEmployeeRoleByAuthUserId(userId);

        if (error) {
          setRole("employee");
          return;
        }

        const nextRole = (emp?.company_role as CompanyRole) ?? "employee";
        setRole(nextRole);
        saveCachedRole(nextRole);
      } finally {
        setRoleLoading(false);
      }
    }

    loadRole();

    const { data: sub } = onAuthStateChange(() => {
      loadRole();
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  const canSeeManager = role === "manager" || role === "admin";
  const itemsMain = role === "admin" ? [] : employeeItems;
  const itemManager: NavItem = {
    label:
      role === "admin"
        ? lang === "de"
          ? "Admin-Ansicht"
          : "Admin view"
        : t("nav.managerView"),
    page: "Manager",
    icon: <AdminPanelSettingsOutlinedIcon />,
  };

  const canSeeUserRights = role === "admin";
  const itemUserRights: NavItem = {
    label: t("nav.userRights"),
    page: "AdminUsers",
    icon: <ManageAccountsOutlinedIcon />,
  };

  const canSeeCompanies = role === "admin";
  const itemCompanies: NavItem = {
    label: t("nav.companies"),
    page: "AdminCompanies",
    icon: <ApartmentOutlinedIcon />,
  };

  const canSeeEvents = role === "admin";
  const itemEvents: NavItem = {
    label: t("nav.events"),
    page: "AdminEvents",
    icon: <EventOutlinedIcon />,
  };

  const canSeeFeedback = role === "admin";
  const itemFeedback: NavItem = {
    label: t("nav.feedback"),
    page: "AdminFeedback",
    icon: <RateReviewOutlinedIcon />,
  };

  // If someone is currently on an admin-only page but lacks permission -> kick them out.
  React.useEffect(() => {
    if (roleLoading) return;
    if (currentPage === "Manager" && !canSeeManager) {
      handlePageChange("KontrolScore");
    } else if (currentPage === "AdminUsers" && !canSeeUserRights) {
      handlePageChange("KontrolScore");
    } else if (currentPage === "AdminCompanies" && !canSeeCompanies) {
      handlePageChange("KontrolScore");
    } else if (currentPage === "AdminEvents" && !canSeeEvents) {
      handlePageChange("KontrolScore");
    } else if (currentPage === "AdminFeedback" && !canSeeFeedback) {
      handlePageChange("KontrolScore");
    }
  }, [
    roleLoading,
    currentPage,
    canSeeManager,
    canSeeUserRights,
    canSeeCompanies,
    canSeeEvents,
    canSeeFeedback,
    handlePageChange,
  ]);

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        height: 68,
        justifyContent: "center",
        background: bg,
        borderBottom: "1px solid #00000014",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: 68,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* LEFT: Logo */}
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 180 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }} >
            <Box component="img" src="/logo.png" alt="takeKONTROL" sx={{ height: 40, width: "auto" }} />
          </Box>
        </Box>

        {/* CENTER: main nav + separated manager (desktop) */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {itemsMain.map((it) => {
            const selected = isSelected(it.page);
            return (
              <Button key={it.page} onClick={() => go(it.page)} startIcon={it.icon} sx={navBtnSx(selected)}>
                {it.label}
              </Button>
            );
          })}

          {/* Only show manager section if allowed */}
          {canSeeManager && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 1.25, borderColor: "#0000001A", alignSelf: "stretch" }}
              />

              <Button
                onClick={() => go(itemManager.page)}
                startIcon={itemManager.icon}
                sx={navBtnSx(isSelected(itemManager.page))}
              >
                {itemManager.label}
              </Button>
            </>
          )}

          {/* Only show user-rights section if allowed */}
          {canSeeUserRights && (
            <Button
              onClick={() => go(itemUserRights.page)}
              startIcon={itemUserRights.icon}
              sx={navBtnSx(isSelected(itemUserRights.page))}
            >
              {itemUserRights.label}
            </Button>
          )}

          {/* Only show companies section if allowed */}
          {canSeeCompanies && (
            <Button
              onClick={() => go(itemCompanies.page)}
              startIcon={itemCompanies.icon}
              sx={navBtnSx(isSelected(itemCompanies.page))}
            >
              {itemCompanies.label}
            </Button>
          )}

          {/* Only show events section if allowed */}
          {canSeeEvents && (
            <Button
              onClick={() => go(itemEvents.page)}
              startIcon={itemEvents.icon}
              sx={navBtnSx(isSelected(itemEvents.page))}
            >
              {itemEvents.label}
            </Button>
          )}

          {/* Only show feedback section if allowed */}
          {canSeeFeedback && (
            <Button
              onClick={() => go(itemFeedback.page)}
              startIcon={itemFeedback.icon}
              sx={navBtnSx(isSelected(itemFeedback.page))}
            >
              {itemFeedback.label}
            </Button>
          )}

          {/* Optional tiny role indicator while loading (remove if you don’t want it) */}
          {roleLoading && (
            <Box sx={{ ml: 1.25, display: "flex", alignItems: "center", gap: 1, color: "#11182799" }}>
              <CircularProgress size={16} />
              <Typography variant="caption" sx={{ fontWeight: 900 }}>
                {t("nav.loading")}
              </Typography>
            </Box>
          )}
        </Box>

        {/* RIGHT: mobile hamburger OR desktop actions */}
        <Box
          sx={{
            minWidth: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          {/* Switch account -- desktop only; folded into the hamburger menu below lg */}
          <Button
            size="small"
            startIcon={<LogoutIcon fontSize="small" />}
            onClick={handleLogout}
            sx={{
              display: { xs: "none", lg: "inline-flex" },
              textTransform: "none",
              fontWeight: 800,
              color: "#374151",
              bgcolor: "#FFFFFFB3",
              border: "1px solid #00000014",
              borderRadius: 999,
              px: 1.25,
            }}
          >
            {t("nav.switchAccount")}
          </Button>

          {/* Language toggle */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={lang}
            onChange={(_, next) => {
              if (next) setLang(next);
            }}
            sx={{
              mr: 0.5,
              bgcolor: "#FFFFFFB3",
              border: "1px solid #00000014",
              borderRadius: 999,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                px: 1.1,
                py: 0.45,
                fontWeight: 900,
                textTransform: "none",
                border: 0,
                color: "#111827B3",
              },
              "& .Mui-selected": {
                color: "#111827",
                bgcolor: "#11182712",
              },
            }}
          >
            <ToggleButton value="en">{t("lang.en")}</ToggleButton>
            <ToggleButton value="de">{t("lang.de")}</ToggleButton>
          </ToggleButtonGroup>

          {/* Mobile hamburger */}
          {isTabletDown ? (
            <>
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                sx={{
                  color: "#111827B3",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "#11182714", color: "#111827" },
                }}
                aria-label="open navigation menu"
              >
                <MenuIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    minWidth: 280,
                    border: "1px solid #00000014",
                    background: "linear-gradient(180deg, #F3F4F6 0%, #ECEFF3 100%)",
                  },
                }}
              >
                <Typography
                  sx={{
                    px: 2,
                    pt: 1.6,
                    pb: 1,
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    color: "#11182799",
                  }}
                >
                  {t("nav.menu")}
                </Typography>

                {itemsMain.map((it) => (
                  <MenuItem
                    key={it.page}
                    selected={isSelected(it.page)}
                    onClick={() => go(it.page)}
                    sx={{
                      mx: 1,
                      my: 0.35,
                      borderRadius: 2,
                      "&.Mui-selected": { bgcolor: "#11182712" },
                      "&.Mui-selected:hover": { bgcolor: "#11182716" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      {it.icon}
                      {it.label}
                    </Box>
                  </MenuItem>
                ))}

                {/* Manager only */}
                {canSeeManager && (
                  <>
                    <Divider sx={{ my: 1, borderColor: "#00000014" }} />
                    <MenuItem
                      selected={isSelected(itemManager.page)}
                      onClick={() => go(itemManager.page)}
                      sx={{
                        mx: 1,
                        my: 0.35,
                        borderRadius: 2,
                        "&.Mui-selected": { bgcolor: "#11182712" },
                        "&.Mui-selected:hover": { bgcolor: "#11182716" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        {itemManager.icon}
                        {itemManager.label}
                      </Box>
                    </MenuItem>
                  </>
                )}

                {/* User rights (admin only) */}
                {canSeeUserRights && (
                  <MenuItem
                    selected={isSelected(itemUserRights.page)}
                    onClick={() => go(itemUserRights.page)}
                    sx={{
                      mx: 1,
                      my: 0.35,
                      borderRadius: 2,
                      "&.Mui-selected": { bgcolor: "#11182712" },
                      "&.Mui-selected:hover": { bgcolor: "#11182716" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      {itemUserRights.icon}
                      {itemUserRights.label}
                    </Box>
                  </MenuItem>
                )}

                {/* Companies (admin only) */}
                {canSeeCompanies && (
                  <MenuItem
                    selected={isSelected(itemCompanies.page)}
                    onClick={() => go(itemCompanies.page)}
                    sx={{
                      mx: 1,
                      my: 0.35,
                      borderRadius: 2,
                      "&.Mui-selected": { bgcolor: "#11182712" },
                      "&.Mui-selected:hover": { bgcolor: "#11182716" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      {itemCompanies.icon}
                      {itemCompanies.label}
                    </Box>
                  </MenuItem>
                )}

                {/* Events (admin only) */}
                {canSeeEvents && (
                  <MenuItem
                    selected={isSelected(itemEvents.page)}
                    onClick={() => go(itemEvents.page)}
                    sx={{
                      mx: 1,
                      my: 0.35,
                      borderRadius: 2,
                      "&.Mui-selected": { bgcolor: "#11182712" },
                      "&.Mui-selected:hover": { bgcolor: "#11182716" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      {itemEvents.icon}
                      {itemEvents.label}
                    </Box>
                  </MenuItem>
                )}

                {/* Feedback (admin only) */}
                {canSeeFeedback && (
                  <MenuItem
                    selected={isSelected(itemFeedback.page)}
                    onClick={() => go(itemFeedback.page)}
                    sx={{
                      mx: 1,
                      my: 0.35,
                      borderRadius: 2,
                      "&.Mui-selected": { bgcolor: "#11182712" },
                      "&.Mui-selected:hover": { bgcolor: "#11182716" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      {itemFeedback.icon}
                      {itemFeedback.label}
                    </Box>
                  </MenuItem>
                )}

                <Divider sx={{ my: 1, borderColor: "#00000014" }} />

                <MenuItem
                  selected={isSelected(itemProfile.page)}
                  onClick={() => go(itemProfile.page)}
                  sx={{
                    mx: 1,
                    my: 0.35,
                    borderRadius: 2,
                    "&.Mui-selected": { bgcolor: "#11182712" },
                    "&.Mui-selected:hover": { bgcolor: "#11182716" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {itemProfile.icon}
                    {itemProfile.label}
                  </Box>
                </MenuItem>

                <MenuItem
                  selected={isSelected(itemEmergency.page)}
                  onClick={() => go(itemEmergency.page)}
                  sx={{
                    mx: 1,
                    my: 0.35,
                    borderRadius: 2,
                    bgcolor: isSelected(itemEmergency.page) ? "#B91C1C14" : "transparent",
                    "&:hover": { bgcolor: "#B91C1C16" },
                    "&.Mui-selected": { bgcolor: "#B91C1C18" },
                    "&.Mui-selected:hover": { bgcolor: "#B91C1C1B" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#B91C1C" }}>
                    <WarningAmberOutlinedIcon />
                    {itemEmergency.label}
                  </Box>
                </MenuItem>

                {!!onHelpClick && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onHelpClick();
                    }}
                    sx={{ mx: 1, my: 0.35, borderRadius: 2 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      <HelpOutlineIcon />
                      {t("nav.help")}
                    </Box>
                  </MenuItem>
                )}

                <Divider sx={{ my: 1, borderColor: "#00000014" }} />

                <MenuItem onClick={handleLogout} sx={{ mx: 1, my: 0.35, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <LogoutIcon />
                    {t("nav.switchAccount")}
                  </Box>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              {/* Desktop right cluster */}
              <Button
                onClick={() => go(itemEmergency.page)}
                startIcon={itemEmergency.icon}
                sx={{
                  textTransform: "none",
                  fontWeight: isSelected(itemEmergency.page) ? 900 : 800,
                  borderRadius: 999,
                  px: 1.6,
                  py: 0.9,
                  minHeight: 42,
                  color: "#B91C1C",
                  bgcolor: isSelected(itemEmergency.page) ? "#B91C1C14" : "#B91C1C10",
                  "&:hover": { bgcolor: "#B91C1C16" },
                  "& .MuiButton-startIcon": { color: "#B91C1C" },
                }}
              >
                {itemEmergency.label}
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, borderColor: "#0000001A" }} />

              <Tooltip title={itemProfile.label} arrow>
                <IconButton
                  onClick={() => go(itemProfile.page)}
                  size="small"
                  sx={{
                    color: isSelected(itemProfile.page) ? "#111827" : "#111827B3",
                    borderRadius: 2,
                    bgcolor: isSelected(itemProfile.page) ? "#11182712" : "transparent",
                    "&:hover": { bgcolor: "#11182714", color: "#111827" },
                  }}
                  aria-label="profile"
                >
                  <PersonOutlineIcon />
                </IconButton>
              </Tooltip>

              {!!onHelpClick && (
                <Tooltip title={t("nav.help")} arrow>
                  <IconButton
                    onClick={onHelpClick}
                    size="small"
                    sx={{
                      color: "#111827B3",
                      borderRadius: 2,
                      "&:hover": { bgcolor: "#11182714", color: "#111827" },
                    }}
                    aria-label="help"
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
